/**
 * Width math for the fused card's side panes. Every share is a percentage of the card, so the
 * layout scales with the window instead of carrying pixel constants.
 */

export type SidePane = 'list' | 'panel';

export interface PaneOpenState {
  isListOpen: boolean;
  isPanelOpen: boolean;
}

/** A pane's share as last set by the teacher, or `null` while it sits at its default. */
export type DraggedShares = Record<SidePane, number | null>;

export type PaneShares = Record<SidePane, number>;

export const MIN_SHARE: Record<SidePane | 'question', number> = {
  list: 20,
  panel: 20,
  question: 30
};

const MAX_SIDE_TOTAL = 100 - MIN_SHARE.question;

const OTHER_PANE: Record<SidePane, SidePane> = {list: 'panel', panel: 'list'};

const isOpen = (pane: SidePane, open: PaneOpenState) =>
  pane === 'list' ? open.isListOpen : open.isPanelOpen;

/** The side panes' floors and the list's opening width, as shares of the card. */
export interface PaneBounds {
  listMin: number;
  listDefault: number;
  panelMin: number;
}

const DEFAULT_BOUNDS: PaneBounds = {
  listMin: MIN_SHARE.list,
  listDefault: MIN_SHARE.list,
  panelMin: MIN_SHARE.panel
};

const minShareOf = (pane: SidePane, bounds: PaneBounds) =>
  pane === 'list' ? bounds.listMin : bounds.panelMin;

/** Pixel limits for the side panes; any left out keep their 20% floor. */
export interface PanePixelBounds {
  listMinWidth?: number;
  listDefaultWidth?: number;
  panelMinWidth?: number;
}

/**
 * Turns pixel limits into shares of a card `cardWidth` wide. When the two floors cannot both fit
 * beside the question area, the panel keeps its floor and the list's gives way.
 */
export const toPaneBounds = (
  cardWidth: number | null,
  {listMinWidth, listDefaultWidth, panelMinWidth}: PanePixelBounds
): PaneBounds | undefined => {
  if (!cardWidth || (!listMinWidth && !listDefaultWidth && !panelMinWidth)) return undefined;
  const shareOf = (px: number | undefined, fallback: number) =>
    px ? (px / cardWidth) * 100 : fallback;
  const panelMin = Math.min(shareOf(panelMinWidth, MIN_SHARE.panel), MAX_SIDE_TOTAL);
  const listMin = Math.min(shareOf(listMinWidth, MIN_SHARE.list), MAX_SIDE_TOTAL - panelMin);
  return {
    listMin,
    listDefault: Math.max(shareOf(listDefaultWidth, MIN_SHARE.list), listMin),
    panelMin
  };
};

/**
 * The list opens at its default width. The panel takes a third with every pane open, or 40% when
 * it is the only side pane. Neither opens below its floor.
 */
export const getDefaultShare = (
  pane: SidePane,
  open: PaneOpenState,
  bounds: PaneBounds = DEFAULT_BOUNDS
): number => {
  if (pane === 'list') return Math.max(bounds.listDefault, bounds.listMin);
  return Math.max(open.isListOpen ? 100 / 3 : 40, bounds.panelMin);
};

/**
 * The share each side pane takes: its dragged share if it has one, else the default for the open
 * set, and 0 while closed. When the two would crowd the question area past its floor, the pane
 * the teacher has not dragged gives way first, and between two undragged panes the panel does.
 */
export const resolvePaneShares = (
  open: PaneOpenState,
  dragged: DraggedShares,
  bounds: PaneBounds = DEFAULT_BOUNDS
): PaneShares => {
  const shares: PaneShares = {list: 0, panel: 0};
  (['list', 'panel'] as const).forEach((pane) => {
    if (isOpen(pane, open)) {
      const draggedShare = dragged[pane];
      // A dragged share can fall under a pixel floor once the window narrows.
      shares[pane] =
        draggedShare === null
          ? getDefaultShare(pane, open, bounds)
          : Math.max(draggedShare, minShareOf(pane, bounds));
    }
  });

  let overflow = shares.list + shares.panel - MAX_SIDE_TOTAL;
  const yieldOrder = (['panel', 'list'] as const)
    .filter((pane) => isOpen(pane, open))
    .sort((a, b) => Number(dragged[a] !== null) - Number(dragged[b] !== null));
  yieldOrder.forEach((pane) => {
    if (overflow <= 0) return;
    const give = Math.min(overflow, shares[pane] - minShareOf(pane, bounds));
    shares[pane] -= give;
    overflow -= give;
  });

  return shares;
};

/** How far a pane can be dragged given the other side pane's current share. */
export const getShareLimits = (
  pane: SidePane,
  shares: PaneShares,
  bounds: PaneBounds = DEFAULT_BOUNDS
) => ({
  min: minShareOf(pane, bounds),
  max: MAX_SIDE_TOTAL - shares[OTHER_PANE[pane]]
});

export const clampShare = (
  pane: SidePane,
  share: number,
  shares: PaneShares,
  bounds: PaneBounds = DEFAULT_BOUNDS
): number => {
  const {min, max} = getShareLimits(pane, shares, bounds);
  return Math.min(Math.max(share, min), max);
};
